export const NEW_MESSAGES_LABEL_CLEAR_DELAY_MS = 1_500;
export const SCROLLED_AWAY_SETTLE_MS = 100;

export type MountPin =
    | { status: "pending" }
    | { status: "pinned"; messageId: string }
    | { status: "closed" };

export function mountPinMessageId(pin: MountPin): string | null {
    return pin.status === "pinned" ? pin.messageId : null;
}

export function resolveMountPinAfterSettle(
    pin: MountPin,
    unreadMessageIds: readonly string[],
    messageIdsNewestFirst: readonly string[],
): MountPin {
    if (pin.status !== "pending") {
        return pin;
    }

    const firstUnreadId = firstLoadedUnreadMessageId(
        unreadMessageIds,
        messageIdsNewestFirst,
    );

    return firstUnreadId
        ? { status: "pinned", messageId: firstUnreadId }
        : { status: "closed" };
}

/** Oldest loaded unread by position in the newest-first message list. */
export function firstLoadedUnreadMessageId(
    unreadMessageIds: readonly string[],
    messageIdsNewestFirst: readonly string[],
): string | null {
    if (unreadMessageIds.length === 0 || messageIdsNewestFirst.length === 0) {
        return null;
    }

    let oldestUnreadId: string | null = null;
    let oldestIndex = -1;

    for (const messageId of unreadMessageIds) {
        const index = messageIdsNewestFirst.indexOf(messageId);
        if (index > oldestIndex) {
            oldestIndex = index;
            oldestUnreadId = messageId;
        }
    }

    return oldestUnreadId;
}

/**
 * Oldest agent message newer than the last read one. Messages the member sent
 * are skipped so sending never raises the member's own unread line.
 */
export function getFirstUnreadMessageId(
    messageIdsNewestFirst: readonly string[],
    lastReadMessageId: string | null,
    agentMessageIds: ReadonlySet<string>,
): string | null {
    const searchFrom = lastReadMessageId
        ? messageIdsNewestFirst.indexOf(lastReadMessageId) - 1
        : messageIdsNewestFirst.length - 1;

    for (let index = searchFrom; index >= 0; index--) {
        const messageId = messageIdsNewestFirst[index];

        if (messageId && agentMessageIds.has(messageId)) {
            return messageId;
        }
    }

    return null;
}

export function selectDisplayedFirstUnreadMessageId({
    showLiveUnreadLine,
    firstLiveUnreadMessageId,
    mountPin,
}: {
    showLiveUnreadLine: boolean;
    firstLiveUnreadMessageId: string | null;
    mountPin: MountPin;
}): string | null {
    return showLiveUnreadLine ? firstLiveUnreadMessageId : mountPinMessageId(mountPin);
}

export function shouldShowUnreadJumpButton({
    atBottom,
    hasLiveUnreadContent,
    mountPin,
}: {
    atBottom: boolean;
    hasLiveUnreadContent: boolean;
    mountPin: MountPin;
}): boolean {
    return !atBottom && (hasLiveUnreadContent || mountPin.status === "pinned");
}
